import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DocumentoLiberacaoPanel from '../components/DocumentoLiberacaoPanel';
import ExecucaoFinanceiraPanel from '../components/ExecucaoFinanceiraPanel';
import { useAuth } from '../context/AuthContext';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import { Permission, UserRole } from '../types/user.types';
import { UI_THEME } from '../ui/theme';

const money = (value?: number) => Number(value ?? 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const identificacao = (emenda: EmendaDTO) =>
  emenda.officialCode || (emenda.numeroEmenda ? `${emenda.numeroEmenda}/${emenda.exercicio ?? ''}` : emenda.id);

const ExecucaoFinanceiraDetailPage: React.FC = () => {
  const { emendaId } = useParams<{ emendaId: string }>();
  const { hasPermission, hasRole } = useAuth();
  const isAdmin = hasRole(UserRole.ADMIN);
  const isConvenios = hasRole(UserRole.CONVENIOS);
  const isOperador = hasRole(UserRole.OPERADOR);
  const isSaas = UI_THEME === 'saas';
  const [emenda, setEmenda] = useState<EmendaDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!emendaId) return;
    setLoading(true);
    setError(null);
    emendaService.getByIdWithDetails(emendaId)
      .then(setEmenda)
      .catch(() => setError('Não foi possível carregar os dados da emenda.'))
      .finally(() => setLoading(false));
  }, [emendaId]);

  if (!emendaId) return <div className="text-sm text-red-600">Emenda não informada.</div>;
  if (loading) return <div className="text-sm text-slate-500">Carregando execução financeira...</div>;
  if (!emenda) return <div className="text-sm text-red-600">{error || 'Emenda não encontrada.'}</div>;

  const panelClass = isSaas
    ? 'rounded-3xl border border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/dashboard/execucao-financeira" className="text-sm font-semibold text-sky-700 hover:underline dark:text-sky-400">
          ← Voltar para Execução Financeira
        </Link>
        <Link to={`/dashboard/emendas/${emendaId}`} className="text-sm font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
          Ver detalhes da emenda
        </Link>
      </div>

      <section className={`${panelClass} p-6`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
          Execução financeira da emenda
        </p>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">{identificacao(emenda)}</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{emenda.institutionName || emenda.secretariaDestino || 'Beneficiário não informado'}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Parlamentar: {emenda.councilorName || 'Não informado'}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Valor da emenda</p>
            <p className="mt-1 text-xl font-bold tabular-nums text-slate-950 dark:text-slate-100">{money(emenda.value)}</p>
          </div>
        </div>
        <dl className="mt-5 grid gap-3 border-t border-slate-200 pt-4 text-sm sm:grid-cols-3 dark:border-slate-700">
          <div><dt className="text-xs uppercase text-slate-500">Transferência</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{emenda.tipoTransferencia || 'Não informada'}</dd></div>
          <div><dt className="text-xs uppercase text-slate-500">Exercício</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{emenda.exercicio || 'Não informado'}</dd></div>
          <div><dt className="text-xs uppercase text-slate-500">Etapa da emenda</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{emenda.statusCicloVida || emenda.status || 'Não informada'}</dd></div>
        </dl>
      </section>

      <DocumentoLiberacaoPanel
        emendaId={emendaId}
        canRequest={isAdmin || isConvenios}
        canSubmit={isAdmin || isOperador}
        canAnalyze={isAdmin || isConvenios}
        onChange={() => setRefreshKey((value) => value + 1)}
      />

      <ExecucaoFinanceiraPanel
        emendaId={emendaId}
        canRegister={{
          EMPENHO: hasPermission(Permission.REGISTER_MOVIMENTACAO_FINANCEIRA),
          LIQUIDACAO: hasPermission(Permission.REGISTER_MOVIMENTACAO_FINANCEIRA),
          PAGAMENTO: hasPermission(Permission.REGISTER_MOVIMENTACAO_FINANCEIRA),
        }}
        canAnnul={{
          EMPENHO: hasPermission(Permission.ANULAR_MOVIMENTACAO_FINANCEIRA),
          LIQUIDACAO: hasPermission(Permission.ANULAR_MOVIMENTACAO_FINANCEIRA),
          PAGAMENTO: hasPermission(Permission.ANULAR_MOVIMENTACAO_FINANCEIRA),
        }}
        refreshKey={refreshKey}
      />
    </div>
  );
};

export default ExecucaoFinanceiraDetailPage;
